/**
 * CR098: Task 1 - DealCardConfigProvider
 * 
 * Single context provider for deal type registry and card templates.
 * Loads config ONCE at app shell level and shares via context.
 * All useDealCardConfig() calls read from context - no per-component fetching.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { DealCategory } from "@/lib/deals/dealTypeRegistry";

// ================================================================
// TYPES
// ================================================================

export interface DealTypeRegistryEntry {
  deal_type_key: string;
  label: string;
  ribbon_text: string;
  ribbon_theme: string;
  is_enabled: boolean;
  sort_order: number;
  updated_by: string | null;
  updated_at: string;
  show_flyer_evidence?: boolean; // CR333
}

export interface CardTemplateSections {
  headline: boolean;
  promo_badges: boolean;
  details_pills: boolean;
  dates: boolean;
  effective_value_box: boolean;
  qualifying_spend_box: boolean;
  reward_box: boolean;
  eligibility_box: boolean;
  source_buttons: boolean;
  why_deal_accordion: boolean;
}

export interface CardTemplateLabels {
  /** CR450: Canonical component-scoped keys */
  value_box_title?: string;
  reward_box_title?: string;
  qualifying_spend_box_title?: string;
  eligibility_box_title?: string;
  /** CR313/CR450: Legacy keys (kept for backward compat) */
  value_title?: string;
  reward_title?: string;
  qualifying_spend_title?: string;
  eligibility_title?: string;
  /** Legacy key - bridged to value_box_title */
  effective_value_title?: string;
  /** Allow additional keys */
  [key: string]: string | undefined;
}

export interface CardTemplateJson {
  sections: CardTemplateSections;
  labels: CardTemplateLabels;
}

export interface DealCardTemplate {
  deal_type_key: string;
  template_json: CardTemplateJson;
  updated_by: string | null;
  updated_at: string;
}

// Default template sections (fallback if DB has no config)
const DEFAULT_SECTIONS: CardTemplateSections = {
  headline: true,
  promo_badges: true,
  details_pills: true,
  dates: true,
  effective_value_box: false,
  qualifying_spend_box: false,
  reward_box: false,
  eligibility_box: false,
  source_buttons: true,
  why_deal_accordion: true,
};

// Default registry fallbacks (for when DB is unavailable)
const DEFAULT_REGISTRY: Record<string, Omit<DealTypeRegistryEntry, "updated_by" | "updated_at">> = {
  BONUS: { deal_type_key: "BONUS", label: "Bonus", ribbon_text: "BONUS", ribbon_theme: "blue", is_enabled: true, sort_order: 10 },
  DISCOUNT: { deal_type_key: "DISCOUNT", label: "Discount", ribbon_text: "% BACK", ribbon_theme: "orange", is_enabled: true, sort_order: 20 },
  POINTS: { deal_type_key: "POINTS", label: "Points", ribbon_text: "POINTS", ribbon_theme: "purple", is_enabled: true, sort_order: 30 },
  IN_KIND: { deal_type_key: "IN_KIND", label: "In-Kind", ribbon_text: "FREE", ribbon_theme: "green", is_enabled: true, sort_order: 40 },
  UNCLASSIFIED: { deal_type_key: "UNCLASSIFIED", label: "Unclassified", ribbon_text: "DEAL", ribbon_theme: "gray", is_enabled: true, sort_order: 50 },
};

// Ribbon theme to Tailwind class mapping
export const RIBBON_THEME_CLASSES: Record<string, string> = {
  blue: "bg-blue-600",
  orange: "bg-orange-500",
  purple: "bg-purple-600",
  green: "bg-green-600",
  gray: "bg-gray-500",
  navy: "bg-slate-800",
  red: "bg-red-600",
  amber: "bg-amber-600",
  emerald: "bg-emerald-600",
  teal: "bg-teal-600",
};

// Valid canonical deal type keys
const CANONICAL_TYPES = new Set(["BONUS", "DISCOUNT", "POINTS", "IN_KIND", "UNCLASSIFIED"]);

// ================================================================
// CONTEXT
// ================================================================

interface DealCardConfigContextValue {
  registry: DealTypeRegistryEntry[];
  templates: DealCardTemplate[];
  loading: boolean;
  configLoaded: boolean;
  error: string | null;
  lastUpdatedAt: string | null;
  
  // Helpers
  getRegistryForType: (dealType: DealCategory | string) => DealTypeRegistryEntry | null;
  getTemplateForType: (dealType: DealCategory | string) => CardTemplateJson;
  getRibbonClass: (dealType: DealCategory | string) => string;
  isTypeEnabled: (dealType: DealCategory | string) => boolean;
  isKnownType: (dealType: DealCategory | string) => boolean;
  getAllowedTypes: () => string[];
  
  // Refresh
  refetch: () => Promise<void>;
}

const DealCardConfigContext = createContext<DealCardConfigContextValue | null>(null);

// ================================================================
// PROVIDER
// ================================================================

interface DealCardConfigProviderProps {
  children: ReactNode;
}

export function DealCardConfigProvider({ children }: DealCardConfigProviderProps) {
  const [registry, setRegistry] = useState<DealTypeRegistryEntry[]>([]);
  const [templates, setTemplates] = useState<DealCardTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log("[DealCardConfigProvider] Fetching config from DB...");
      
      const [registryResult, templatesResult] = await Promise.all([
        supabase.from("deal_type_registry").select("*").order("sort_order", { ascending: true }),
        supabase.from("deal_card_templates").select("*"),
      ]);
      
      if (registryResult.error) {
        console.warn("[DealCardConfigProvider] Registry fetch error:", registryResult.error);
        setError(`Registry fetch failed: ${registryResult.error.message}`);
      } else {
        setRegistry(registryResult.data || []);
        // Track latest updated_at
        const timestamps = (registryResult.data || []).map(r => r.updated_at).filter(Boolean);
        if (timestamps.length > 0) {
          timestamps.sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
          setLastUpdatedAt(timestamps[0]);
        }
      }
      
      if (templatesResult.error) {
        console.warn("[DealCardConfigProvider] Templates fetch error:", templatesResult.error);
      } else {
        // Cast the template_json from Json to CardTemplateJson
        const typedTemplates = (templatesResult.data || []).map(t => ({
          ...t,
          template_json: t.template_json as unknown as CardTemplateJson,
        }));
        setTemplates(typedTemplates);
      }
      
      setConfigLoaded(true);
      console.log(`[DealCardConfigProvider] Loaded ${registryResult.data?.length || 0} registry entries, ${templatesResult.data?.length || 0} templates`);
    } catch (err) {
      console.error("[DealCardConfigProvider] Unexpected error:", err);
      setError(String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Get registry entry for a deal type (with fallback)
  const getRegistryForType = useCallback((dealType: DealCategory | string): DealTypeRegistryEntry | null => {
    const key = dealType.toUpperCase();
    const fromDb = registry.find(r => r.deal_type_key === key);
    
    if (fromDb) return fromDb;
    
    // Fallback to defaults
    const defaultEntry = DEFAULT_REGISTRY[key];
    if (defaultEntry) {
      return {
        ...defaultEntry,
        updated_by: null,
        updated_at: new Date().toISOString(),
      };
    }
    
    // Unknown type - return UNCLASSIFIED config
    return {
      ...DEFAULT_REGISTRY.UNCLASSIFIED,
      deal_type_key: key,
      updated_by: null,
      updated_at: new Date().toISOString(),
    };
  }, [registry]);

  // Get template for a deal type (with fallback)
  const getTemplateForType = useCallback((dealType: DealCategory | string): CardTemplateJson => {
    const key = dealType.toUpperCase();
    const fromDb = templates.find(t => t.deal_type_key === key);
    
    if (fromDb?.template_json) {
      return {
        sections: { ...DEFAULT_SECTIONS, ...fromDb.template_json.sections },
        labels: fromDb.template_json.labels || {},
      };
    }
    
    // Return defaults
    return {
      sections: DEFAULT_SECTIONS,
      labels: {},
    };
  }, [templates]);

  // Get ribbon Tailwind class for a deal type
  const getRibbonClass = useCallback((dealType: DealCategory | string): string => {
    const regEntry = getRegistryForType(dealType);
    const theme = regEntry?.ribbon_theme || "gray";
    return RIBBON_THEME_CLASSES[theme] || RIBBON_THEME_CLASSES.gray;
  }, [getRegistryForType]);

  // Check if a deal type is enabled
  const isTypeEnabled = useCallback((dealType: DealCategory | string): boolean => {
    const regEntry = getRegistryForType(dealType);
    return regEntry?.is_enabled ?? true;
  }, [getRegistryForType]);

  // CR098 Task 3: Check if a deal type is known (in registry)
  const isKnownType = useCallback((dealType: DealCategory | string): boolean => {
    const key = dealType.toUpperCase();
    
    // Check DB registry first
    if (registry.length > 0) {
      return registry.some(r => r.deal_type_key === key);
    }
    
    // Fall back to canonical types if DB not loaded
    return CANONICAL_TYPES.has(key);
  }, [registry]);

  // Get all allowed (enabled) type keys
  const getAllowedTypes = useCallback((): string[] => {
    if (registry.length === 0) {
      return Array.from(CANONICAL_TYPES);
    }
    return registry.filter(r => r.is_enabled).map(r => r.deal_type_key);
  }, [registry]);

  const contextValue: DealCardConfigContextValue = {
    registry,
    templates,
    loading,
    configLoaded,
    error,
    lastUpdatedAt,
    getRegistryForType,
    getTemplateForType,
    getRibbonClass,
    isTypeEnabled,
    isKnownType,
    getAllowedTypes,
    refetch: fetchConfig,
  };

  return (
    <DealCardConfigContext.Provider value={contextValue}>
      {children}
    </DealCardConfigContext.Provider>
  );
}

// ================================================================
// HOOK - reads from context, no refetching
// ================================================================

export function useDealCardConfig(): DealCardConfigContextValue {
  const context = useContext(DealCardConfigContext);
  
  if (!context) {
    throw new Error("useDealCardConfig must be used within DealCardConfigProvider");
  }
  
  return context;
}

// ================================================================
// STANDALONE HELPERS (for edge functions / non-React contexts)
// ================================================================

let standaloneRegistry: DealTypeRegistryEntry[] = [];
let standaloneTemplates: DealCardTemplate[] = [];
let standaloneLoaded = false;

/**
 * Load config for standalone use (call from edge functions or non-React code)
 */
export async function loadStandaloneConfig(): Promise<void> {
  if (standaloneLoaded) return;
  
  const [registryResult, templatesResult] = await Promise.all([
    supabase.from("deal_type_registry").select("*").order("sort_order", { ascending: true }),
    supabase.from("deal_card_templates").select("*"),
  ]);
  
  standaloneRegistry = registryResult.data || [];
  standaloneTemplates = (templatesResult.data || []).map(t => ({
    ...t,
    template_json: t.template_json as unknown as CardTemplateJson,
  }));
  standaloneLoaded = true;
}

/**
 * Get all enabled types for publish gate validation
 */
export function getStandaloneEnabledTypes(): string[] {
  if (standaloneRegistry.length === 0) {
    return Array.from(CANONICAL_TYPES);
  }
  return standaloneRegistry.filter(r => r.is_enabled).map(r => r.deal_type_key);
}

/**
 * Check if type is known in standalone context
 */
export function isStandaloneKnownType(dealType: string): boolean {
  const key = dealType.toUpperCase();
  if (standaloneRegistry.length > 0) {
    return standaloneRegistry.some(r => r.deal_type_key === key);
  }
  return CANONICAL_TYPES.has(key);
}
